import { useI18n } from 'vue-i18n'

/**
 * 获取多语言图片路径
 * @param imageKey 图片键名，例如 'sidebar.indiqueAmigos'
 * @returns 图片路径
 */
export const getMultilingualImage = (imageKey: string): string => {
  const { t } = useI18n()
  
  try {
    // 从语言包中获取图片路径
    const imagePath = t(`images.${imageKey}`)
    
    // 如果找到了图片路径，使用 getAssetsFile 获取完整路径
    if (imagePath && imagePath !== `images.${imageKey}`) {
      return getAssetsFile(imagePath)
    }
    
    // 如果语言包中没有配置，返回默认路径
    return getAssetsFile(imageKey)
  } catch (error) {
    console.warn(`Failed to get multilingual image for key: ${imageKey}`, error)
    return getAssetsFile(imageKey)
  }
}

/**
 * 获取侧边栏多语言图片
 * @param type 图片类型：'indiqueAmigos' | 'comissaoDe'
 * @returns 图片路径
 */
export const getSidebarImage = (type: 'indiqueAmigos' | 'comissaoDe'): string => {
  return getMultilingualImage(`sidebar.${type}`)
}

/**
 * 解析JSON格式的多语言图片数据
 * @param imageData JSON字符串或对象，格式如: {"en":"hash-admin/de7704ca-8dbe-45b8-aec9-56025bf6329b.jpg","id":"hash-admin/b44cdf88-428b-4a7c-a1e7-fdd08cc342e5.png"}
 * @param currentLang 当前语言代码，如果不传则默认为 'en'
 * @returns 根据当前语言返回对应的图片路径
 */
export const parseMultilingualImageData = (imageData: string | object, currentLang?: string): string => {
  try {
    // 如果是字符串，尝试解析为JSON
    const data = typeof imageData === 'string' ? JSON.parse(imageData) : imageData
    
    // 使用传入的语言参数，如果没有则默认为 'en'
    const lang = currentLang || 'en'
    
    // 语言代码映射 - 同时支持多种可能的键名
    const langMapping: Record<string, string[]> = {
      'en-US': ['en', 'en-US'],
      'hi-IN': ['id', 'hi-IN', 'hi'], // 印地语支持多种可能的键名
      'pt-BR': ['pt', 'pt-BR', 'pt-BR'], // 添加葡萄牙语映射
      'tl-PH': ['tl', 'tl-PH'], // 菲律宾语支持
      'en': ['en', 'en-US'],
      'id': ['id', 'hi-IN', 'hi'],
      'pt': ['pt', 'pt-BR', 'pt-BR'], // 添加葡萄牙语映射
      'tl': ['tl', 'tl-PH'] // 菲律宾语简化代码
    }
    
    // 获取映射后的语言代码数组
    const mappedLangs = langMapping[lang] || [lang]
    
    // 按优先级尝试不同的语言代码
    for (const langCode of mappedLangs) {
      if (data[langCode]) {
        return data[langCode]
      }
    }
    
    // 如果当前语言不存在，返回英文版本作为默认
    if (data.en) {
      return data.en
    }
    
    // 如果英文版本也不存在，返回第一个可用的图片
    const firstLang = Object.keys(data)[0]
    if (firstLang) {
      return data[firstLang]
    }
    
    // 如果都没有，返回空字符串
    return ''
  } catch (error) {
    console.warn('解析多语言图片数据失败:', error)
    // 如果解析失败，直接返回原始数据（如果是字符串的话）
    return typeof imageData === 'string' ? imageData : ''
  }
} 